import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { portfolioProjects } from '../data/mock';

const Portfolio = () => {
  const { t } = useTranslation();
  const categories = ['Alle', 'Webseite', 'Web App', 'E-Commerce'];
  const [activeCategory, setActiveCategory] = useState('Alle');

  const filteredProjects = activeCategory === 'Alle'
    ? portfolioProjects
    : portfolioProjects.filter(project => project.category === activeCategory);

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            {t('portfolio.title')}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Portfolio &
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> {t('portfolio.subtitle')}</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('portfolio.description')}
          </p>
        </div>
      </section>

      {/* Filter & Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-200 ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-block px-3 py-1 bg-blue-600 text-white text-sm rounded-full mb-3">
                        {project.category}
                      </span>
                      <div className="flex gap-2">
                        <Link to={project.demoUrl}>
                          <Button size="sm" className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white text-xs">
                            Live Demo
                            <ExternalLink className="ml-1" size={14} />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-blue-600 font-medium">{project.client}</span>
                    <span className="text-sm text-slate-400">{project.year}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{project.title}</h3>
                  <p className="text-slate-600 mb-4 line-clamp-2">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={project.demoUrl}
                    className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 text-sm group/link"
                  >
                    Live Demo ansehen
                    <ExternalLink className="ml-1 group-hover/link:translate-x-0.5 transition-transform" size={14} />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">Keine Projekte in dieser Kategorie gefunden.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '200+', label: 'Projekte abgeschlossen' },
              { value: '150+', label: 'Zufriedene Kunden' },
              { value: '15+', label: 'Branchen bedient' },
              { value: '99%', label: 'Kundenzufriedenheit' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Möchten Sie das nächste Erfolgs-Projekt sein?
          </h2>
          <p className="text-slate-600 mb-8 text-lg max-w-2xl mx-auto">
            Wir freuen uns darauf, auch Ihr Projekt zu einem Erfolg zu machen. Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              Projekt besprechen
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
